import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { alpha } from "@mui/material/styles";

const KGS_TO_LBS = 2.20462;

const styles = {
  closeButton: {
    position: "absolute",
    right: 18,
    top: 18,
    color: "text.secondary",
  },
  unitToggle: {
    width: { xs: 150, sm: 220 },
    flexShrink: 0,
    height: 40,
    backgroundColor: "common.white",
    borderRadius: 999,
    overflow: "hidden",
    "& .MuiToggleButton-root": {
      flex: 1,
      borderColor: "divider",
      "&.Mui-selected": {
        backgroundColor: "#1976d2",
        color: "#fff",
        "&:hover": {
          backgroundColor: "#1565c0",
        },
      },
    },
    "& .MuiToggleButtonGroup-grouped": {
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "0 !important",
      px: { xs: 1, sm: 2 },
      py: 0,
      fontSize: 14,
      fontWeight: 700,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: 0.2,
    },
  },
  divider: {
    color: "text.secondary",
    fontSize: 22,
    px: 1,
    userSelect: "none",
  },
  resultCard: {
    mt: 3,
    px: 2,
    py: 1.5,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "primary.light",
    backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
  },
  button: {
    minWidth: 96,
  },
  calculatorButton: {
    p: 0,
    minWidth: 0,
    justifyContent: "flex-start",
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
  },
};

const toNumber = value => {
  if (value === "" || value === null || value === undefined) return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundWeight = value => Math.round((value + Number.EPSILON) * 100) / 100;

const formatDraftWeight = value => {
  if (value == null || !Number.isFinite(value)) return "";

  return String(roundWeight(value));
};

export default function WeightModal({ title, initialValue, onClose, onSubmit }) {
  const [unit, setUnit] = useState("lbs");
  const [calculatorMode, setCalculatorMode] = useState(false);
  const [weight, setWeight] = useState(() => {
    const initial = toNumber(initialValue);
    return initial == null ? "" : String(initial);
  });
  const [divisor, setDivisor] = useState("");

  const result = useMemo(() => {
    const weightValue = toNumber(weight);
    if (weightValue == null) return null;

    let value = weightValue;

    if (calculatorMode) {
      const divisorValue = toNumber(divisor);
      if (divisorValue == null || divisorValue === 0) return null;
      value = weightValue / divisorValue;
    }

    if (unit === "kgs") {
      value *= KGS_TO_LBS;
    }

    if (!Number.isFinite(value) || value < 0) return null;

    return roundWeight(value);
  }, [weight, divisor, unit, calculatorMode]);

  const handleUnitChange = (event, nextUnit) => {
    if (!nextUnit || nextUnit === unit) return;

    const weightValue = toNumber(weight);
    if (weightValue != null) {
      setWeight(formatDraftWeight(nextUnit === "kgs" ? weightValue / KGS_TO_LBS : weightValue * KGS_TO_LBS));
    }
    setUnit(nextUnit);
  };

  const handleSave = () => {
    if (result == null) return;
    onSubmit(result);
  };

  const unitLabel = unit === "kgs" ? "kgs" : "lbs";

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: 520,
          maxWidth: "calc(100% - 32px)",
          borderRadius: 3,
        },
      }}
    >
      <DialogContent sx={{ px: 4, py: 3.5, position: "relative" }}>
        <IconButton onClick={onClose} sx={styles.closeButton} title="Close">
          <CloseIcon />
        </IconButton>
        <Typography fontSize={24} fontWeight={500} color="text.primary" align="center" sx={{ mb: 1.5 }}>
          {title}
        </Typography>
        <Typography
          fontSize={15}
          color="text.secondary"
          align="center"
          sx={{ maxWidth: 420, mx: "auto", lineHeight: 1.45, mb: 3 }}
        >
          The weight entered here should reflect how much each unit of measure weighs.
        </Typography>

      {calculatorMode ? (
        <Box>
          <Box
            display="flex"
            alignItems="flex-start"
            gap={2}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              label={`Total Weight (${unitLabel})`}
              value={weight}
              onChange={event => setWeight(event.target.value)}
              inputProps={{ inputMode: "decimal" }}
              sx={{ minWidth: 0 }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={unit}
              onChange={handleUnitChange}
              sx={styles.unitToggle}
            >
              <ToggleButton value="lbs">LBS</ToggleButton>
              <ToggleButton value="kgs">KGS</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={styles.divider} mt={1}>
            ÷
          </Box>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Divide by"
            value={divisor}
            onChange={event => setDivisor(event.target.value)}
            inputProps={{ inputMode: "decimal" }}
          />
        </Box>
      ) : (
        <Box
          display="flex"
          alignItems="flex-start"
          gap={2}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            label={`Weight (${unitLabel})`}
            value={weight}
            onChange={event => setWeight(event.target.value)}
            inputProps={{ inputMode: "decimal" }}
            sx={{ minWidth: 0 }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={unit}
            onChange={handleUnitChange}
            sx={styles.unitToggle}
          >
            <ToggleButton value="lbs">LBS</ToggleButton>
            <ToggleButton value="kgs">KGS</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box sx={styles.resultCard}>
        <Typography variant="body2" color="text.secondary">
          Saves as
        </Typography>
        <Typography fontWeight={600} color="primary.main">
          {result == null ? "—" : `${result.toLocaleString()} lbs`}
        </Typography>
      </Box>

      <Box mt={3} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Button
          color="primary"
          onClick={() => setCalculatorMode(value => !value)}
          sx={styles.calculatorButton}
        >
          {calculatorMode ? "Disable Weight Calculator" : "Enable Weight Calculator"}
        </Button>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={result == null}
            sx={styles.button}
          >
            Save
          </Button>
        </Box>
      </Box>
      </DialogContent>
    </Dialog>
  );
}
